import { Injectable } from "@angular/core";

import {
    HtmlText,
    HtmlSequence,
    HtmlDirective,
    HtmlExpression,
    HtmlDirectiveParser,
    HtmlExpressionParserState,
    HtmlExpressionParserSettings,
} from "@main/reporting.v3/widget.html/models";


@Injectable()
export class HtmlBaseExpressionsParserService {

    public parse(source: string, settings?: HtmlExpressionParserSettings): HtmlSequence {
        if (!source) return null;

        const state: HtmlExpressionParserState = { source: source, index: 0, length: source.length };

        return this.parseSequence(state, settings);
    }

    public parseSequence(state: HtmlExpressionParserState, settings: HtmlExpressionParserSettings): HtmlSequence {
        if (!state || !settings) return;

        const results: HtmlExpression[] = [];

        while (state.index < state.length) {
            const text: HtmlText = this.readText(state);

            if (text) results.push(text);

            if (state.index >= state.length) return this.toSequence(results);

            const current: string = state.source[state.index];

            if (current === "@") {
                // If a directive starts at this position.
                const name: string = this.lookIdentifier(state);

                if (settings.directives.includes(name)) {
                    // If it is a registered directive.

                    // Parse the directive with the corresponding parser if any.
                    // Otherwise, use default parser for directives.
                    const parser: HtmlDirectiveParser = settings.parsers?.[name];

                    const directive: HtmlDirective = !parser
                        ? this.parseDirective(state, settings)
                        : parser?.parse(state, settings);

                    if (!!directive) {
                        // If the directive was parsed, add it to the list of results.

                        if (!directive.block && settings.structuralOnly) {
                            // Treat it as plain text instead if this is a non-structural directive
                            // (it has no content block) and only structural directives are expected.
                            results.push(this.toText(state, directive));
                        } else {
                            // Otherwise, add it to the list of results as it is.
                            results.push(directive);
                        }
                        continue;
                    }
                }
            }

            // If no directive starts at this position, create a text expression with this character only (to not trim it).
            results.push(new HtmlText(current, state.index, state.index + 1));
            state.index++;
        }

        return this.toSequence(results);
    }

    public parseDirective(state: HtmlExpressionParserState, settings: HtmlExpressionParserSettings): HtmlDirective {
        if (!state || !settings) return;

        const start: number = state.index;

        if (state.source[state.index] !== "@") return null;

        const name: string = this.readIdentifier(state);
        if (!name) return null;

        const args: HtmlSequence[] =
            this.shouldParseArgs(state, settings)
                ? this.parseArguments(state, settings)
                : null;

        const block: HtmlSequence =
            this.shouldParseBlock(state, settings)
                ? this.parseBlock(state, settings)
                : null;

        return new HtmlDirective(name, start, state.index, args, block);
    }

    public parseArguments(state: HtmlExpressionParserState, settings: HtmlExpressionParserSettings): HtmlSequence[] {
        if (!state || !settings) return;

        this.skipWhitespaces(state);

        const results: HtmlSequence[] = [];

        for (let index: number = state.index, depth: number = 0; index < state.source.length; index++) {

            if (state.source[index] === "(") depth++;
            if (state.source[index] === ")") depth--;

            if (depth > 1) continue;

            const separator: string = settings.argumentSeparator ?? ",";

            if (depth === 1 && state.source[index] !== separator) continue;
            if (depth === 0 && state.source[index] !== ")") continue;

            // Parse the argument content recursively.
            results.push(this.parseSequence(
                {
                    ...state,
                    length: index,
                    index: state.index + 1,
                },
                {
                    ...settings,
                    forbidBlocks: true,
                    structuralOnly: false,
                    argumentSeparator: null,
                }
            ));

            state.index = index;

            if (depth > 0) continue;

            state.index++;

            return results;
        }

        return null;
    }

    public parseBlock(state: HtmlExpressionParserState, settings: HtmlExpressionParserSettings): HtmlSequence {
        if (!state || !settings) return;

        this.skipWhitespaces(state);

        for (let index: number = state.index, depth: number = 0; index < state.source.length; index++) {

            if (state.source[index] === "{") depth++;
            if (state.source[index] === "}") depth--;

            if (depth > 0) continue;

            // Parse the block content recursively.
            const block: HtmlSequence = this.parseSequence(
                {
                    ...state,
                    length: index,
                    index: state.index + 1,
                },
                {
                    ...settings,
                    forbidBlocks: false,
                    forbidArguments: false,
                    argumentSeparator: null,
                }
            );

            state.index = index + 1;

            return block;
        }

        return null;
    }

    public shouldParseBlock(state: HtmlExpressionParserState, settings: HtmlExpressionParserSettings): boolean {
        if (!state || !settings) return false;

        if (settings.forbidBlocks) return false;

        return !!state.source.slice(state.index).match(/^\s*\{/);
    }

    public shouldParseArgs(state: HtmlExpressionParserState, settings: HtmlExpressionParserSettings): boolean {
        if (!state || !settings) return false;

        if (settings.forbidArguments) return false;

        return !!state.source.slice(state.index).match(/^\s*\(/);
    }

    public toText(state: HtmlExpressionParserState, directive: HtmlDirective): HtmlText {
        if (!state || !directive) return null;

        const content: string = state.source.slice(directive.start, directive.end);

        return new HtmlText(content, directive.start, directive.end);
    }

    public toSequence(expressions: HtmlExpression[]): HtmlSequence {
        if (!expressions?.length) return null;

        const start: number = expressions[0].start;
        const end: number = expressions[expressions.length - 1].end;

        return new HtmlSequence(expressions, start, end);
    }

    public lookIdentifier(state: HtmlExpressionParserState): string {
        if (!state) return null;

        // Ensure from this position onwards there is a valid directive identifier followed
        // by an open parenthesis or brace (a directive must have arguments or code block).
        const regex: RegExp = /^@(?<identifier>[A-Za-z_]+)(?=(\s*[({]))/;

        const text: string = state.source.slice(state.index);

        const match: RegExpMatchArray = regex.exec(text);

        return !!match ? match[1] : null;
    }

    public skipWhitespaces(state: HtmlExpressionParserState): void {
        if (!state) return;

        const regex: RegExp = /[ \t\n\r]/;

        while (state.index < state.length && regex.test(state.source[state.index])) state.index++;
    }

    public readIdentifier(state: HtmlExpressionParserState): string {
        if (!state) return null;

        const identifier: string = this.lookIdentifier(state);

        if (!identifier?.length) return null;

        // Skip the @ character and the whole identifier.
        state.index += identifier.length + 1;

        return identifier;
    }

    public readText(state: HtmlExpressionParserState): HtmlText {
        if (!state) return null;

        const start: number = state.index;

        while (state.index < state.length && state.source[state.index] !== "@") state.index++;

        if (state.index === start) return null;

        const content: string = state.source.slice(start, state.index);

        return new HtmlText(content, start, state.index);
    }
}